import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

/* -------------------------------------------------- */
/* CARD ELEMENT SHARED STYLE                          */
/* -------------------------------------------------- */

const cardElementStyle = {
  base: {
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: "15px",
    fontWeight: "400",
    color: "#122136",
    letterSpacing: "0.01em",
    "::placeholder": {
      color: "#94a3b8",
      fontWeight: "300",
    },
  },
  invalid: {
    color: "#e53e3e",
    iconColor: "#e53e3e",
  },
  complete: {
    color: "#122136",
  },
};

/* -------------------------------------------------- */
/* FIELD WRAPPER                                      */
/* -------------------------------------------------- */

const FieldWrapper = ({ label, children, error }) => (
  <div className="flex flex-col gap-1.5 flex-1">
    <label className="text-[11px] font-bold uppercase tracking-widest text-navy/60 font-heading">
      {label}
    </label>
    <div
      className={`border rounded-xl px-3.5 py-3 bg-white transition-all duration-150
        ${
          error
            ? "border-red-400 shadow-[0_0_0_3px_rgba(229,62,62,0.08)]"
            : "border-navy/50 focus-within:border-gold focus-within:shadow-[0_0_0_3px_rgba(212,175,55,0.12)]"
        }`}
    >
      {children}
    </div>
    {error && <p className="text-[12px] text-red-500 leading-snug">{error}</p>}
  </div>
);

/* -------------------------------------------------- */
/* CHECKOUT FORM                                      */
/* -------------------------------------------------- */

const CheckoutForm = ({
  amount,
  currency,
  email,
  clientSecret,
  onSuccess,
  onCancel,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [cardNumberError, setCardNumberError] = useState(null);
  const [cardExpiryError, setCardExpiryError] = useState(null);
  const [cardCvcError, setCardCvcError] = useState(null);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardCvcFocused, setCardCvcFocused] = useState(false);

  const allComplete =
    cardNumberComplete && cardExpiryComplete && cardCvcComplete;

  const formatAmount = (val) =>
    Number(val).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || loading) return;

    setLoading(true);
    setErrorMsg(null);

    const cardElement = elements.getElement(CardNumberElement);

    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: { email },
    });

    if (pmError) {
      setErrorMsg(pmError.message || "Payment failed. Please try again.");
      setLoading(false);
      return;
    }

    const { error: confirmError, paymentIntent } =
      await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
          receipt_email: email,
        },
        { redirect: "if_required" }, // ← ADD THIS
      );


    setLoading(false);

    if (confirmError) {
      // Special case — MANUAL capture: payment is authorized, treat as success
      if (confirmError?.payment_intent?.status === "requires_capture") {
        onSuccess({
          success: true,
          paymentIntentId: confirmError.payment_intent.id,
        });
        return;
      }
      // Genuine error — show to user
      setErrorMsg(confirmError.message || "Payment failed. Please try again.");
      return;
    }
    if (
      paymentIntent?.status === "succeeded" ||
      paymentIntent?.status === "requires_capture" // safety fallback
    ) {
      onSuccess({ success: true, paymentIntentId: paymentIntent.id });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      {/* ── Amount badge ── */}
      <div className="flex items-center justify-between bg-primary/90 rounded-xl px-4 py-3.5 mb-5 gap-3">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
            <svg
              width="10"
              height="12"
              className="sm:w-[12px] sm:h-[14px]"
              viewBox="0 0 12 14"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9.5 6V4.5a3.5 3.5 0 10-7 0V6H1v7a1 1 0 001 1h8a1 1 0 001-1V6H9.5zm-6-1.5a2.5 2.5 0 015 0V6h-5V4.5z"
                fill="#22c55e"
              />
            </svg>
          </div>
          <span className="text-white text-xs sm:text-sm font-body font-normal whitespace-nowrap">
            Amount to pay now
          </span>
        </div>
        <span className="text-white font-heading font-bold text-sm sm:text-lg tracking-wide text-right whitespace-nowrap">
          {currency.toUpperCase()} {formatAmount(amount)}
        </span>
      </div>

      {/* ── Accepted cards row ── */}
      <div className="flex items-center justify-between mb-5">
        <span className="text-[11px] font-bold text-navy/60 font-body uppercase tracking-widest">
          Accepted cards
        </span>
        <div className="flex items-center gap-1.5">
          <div className="rounded overflow-hidden opacity-80">
            <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
              <rect width="32" height="20" rx="3" fill="#1A1F71" />
              <text
                x="4"
                y="14"
                fontFamily="Arial"
                fontSize="9"
                fontWeight="bold"
                fill="white"
              >
                VISA
              </text>
            </svg>
          </div>
          <div className="rounded overflow-hidden opacity-80">
            <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
              <rect width="32" height="20" rx="3" fill="#252525" />
              <circle cx="13" cy="10" r="6" fill="#EB001B" />
              <circle cx="19" cy="10" r="6" fill="#F79E1B" />
              <path d="M16 5.8a6 6 0 010 8.4A6 6 0 0116 5.8z" fill="#FF5F00" />
            </svg>
          </div>
          <div className="rounded overflow-hidden opacity-80">
            <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
              <rect width="32" height="20" rx="3" fill="#2E77BC" />
              <text
                x="3"
                y="14"
                fontFamily="Arial"
                fontSize="7"
                fontWeight="bold"
                fill="white"
              >
                AMEX
              </text>
            </svg>
          </div>
          <div className="rounded overflow-hidden opacity-80">
            <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
              <rect width="32" height="20" rx="3" fill="#003087" />
              <text
                x="5"
                y="14"
                fontFamily="Arial"
                fontSize="8"
                fontWeight="bold"
                fill="white"
              >
                JCB
              </text>
            </svg>
          </div>
        </div>
      </div>

      {/* ── Card number — full width ── */}
      <div className="mb-3.5">
        <FieldWrapper label="Card number" error={cardNumberError}>
          <CardNumberElement
            options={{
              style: cardElementStyle,
              showIcon: true,
              disabled: loading,
            }}
            onChange={(e) => {
              setCardNumberError(e.error?.message || null);
              setCardNumberComplete(e.complete);
            }}
          />
        </FieldWrapper>
      </div>

      {/* ── Expiry + CVC — side by side on all screens ── */}
      <div className="grid grid-cols-2 gap-3 mb-3.5">
        <FieldWrapper label="Expiration date" error={cardExpiryError}>
          <CardExpiryElement
            options={{ style: cardElementStyle, disabled: loading }}
            onChange={(e) => {
              setCardExpiryError(e.error?.message || null);
              setCardExpiryComplete(e.complete);
            }}
          />
        </FieldWrapper>
        <FieldWrapper label="Security code" error={cardCvcError}>
          <div className="relative">
            {/* Real Stripe input */}
            <div
              style={{ opacity: cardCvcComplete && !cardCvcFocused ? 0 : 1 }}
            >
              <CardCvcElement
                options={{ style: cardElementStyle, disabled: loading }}
                onFocus={() => setCardCvcFocused(true)}
                onBlur={() => setCardCvcFocused(false)}
                onChange={(e) => {
                  setCardCvcError(e.error?.message || null);
                  setCardCvcComplete(e.complete);
                }}
              />
            </div>
            {/* Masked overlay */}
            {cardCvcComplete && !cardCvcFocused && (
              <div
                className="absolute inset-0 flex items-center cursor-text"
                onClick={() => elements.getElement(CardCvcElement)?.focus()}
              >
                <span className="text-navy/40 tracking-[0.2em] text-sm">
                  •••
                </span>
              </div>
            )}
          </div>
        </FieldWrapper>
      </div>

      {/* ── Global error ── */}
      {errorMsg && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="shrink-0 mt-0.5"
          >
            <circle cx="8" cy="8" r="7.5" stroke="#e53e3e" strokeWidth="1" />
            <path
              d="M8 5v3.5M8 10.5v.5"
              stroke="#e53e3e"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-[13px] text-red-600 leading-relaxed">
            {errorMsg}
          </span>
        </div>
      )}

      {/* ── Buttons ── */}
      <div className="flex flex-col sm:flex-row gap-2.5 mt-1 mb-3.5">
        <button
          type="submit"
          disabled={!stripe || loading || !allComplete}
          className={`flex-1 w-full flex items-center justify-center gap-2
                      py-3.5 rounded-xl text-sm font-bold font-heading
                      tracking-wide text-white transition-all duration-150
                      ${
                        allComplete && !loading
                          ? "bg-primary hover:bg-primary-hover shadow-gold-glow active:scale-[0.98] cursor-pointer"
                          : "bg-primary/100 cursor-not-allowed"
                      }`}
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-primary border-t-primary rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M11 4.5H3a1 1 0 00-1 1V10a1 1 0 001 1h8a1 1 0 001-1V5.5a1 1 0 00-1-1zM3 4.5V4a4 4 0 118 0v.5"
                  stroke="rgb(255, 255, 255)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              Pay {currency.toUpperCase()} {formatAmount(amount)}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-3.5 bg-white border border-navy/50 rounded-xl
             text-sm font-semibold uppercase tracking-widest text-navy/60 font-heading
             hover:bg-red-50 hover:border-red-300 hover:text-red-500
             transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center justify-center gap-1.5 pt-1">
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 1a5 5 0 100 10A5 5 0 006 1zm0 9A4 4 0 116 2a4 4 0 010 8z"
            fill="#94a3b8"
          />
          <path
            d="M6 5.5a.5.5 0 000 1h.5a.5.5 0 000-1H6zM5.5 4a.5.5 0 011 0v.5a.5.5 0 01-1 0V4z"
            fill="#94a3b8"
          />
        </svg>
        <span className="text-[11px] text-navy/30 font-body">
          Your payment is encrypted and secured by{" "}
          <strong className="font-semibold text-navy/40">Stripe</strong>
        </span>
      </div>
    </form>
  );
};

/* -------------------------------------------------- */
/* READY DETECTOR                                     */
/* -------------------------------------------------- */

const ReadyDetector = ({ onReady }) => {
  const elements = useElements();
  useEffect(() => {
    if (elements) {
      const t = setTimeout(onReady, 500);
      return () => clearTimeout(t);
    }
  }, [elements, onReady]);
  return null;
};

/* -------------------------------------------------- */
/* EXPORTED — StripePaymentForm                       */
/* -------------------------------------------------- */

export const StripePaymentForm = ({
  publishableKey,
  clientSecret,
  amount,
  currency,
  email,
  onSuccess,
  onCancel,
}) => {
  const [stripePromise] = useState(() => loadStripe(publishableKey));
  const [ready, setReady] = useState(false);

  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: "none",
      variables: {
        colorPrimary: "#D4AF37",
        colorBackground: "#ffffff",
        colorText: "#122136",
        colorDanger: "#e53e3e",
        fontFamily: "'Inter', system-ui, sans-serif",
        borderRadius: "12px",
      },
    },
  };

  return (
    <div className="mt-5 font-body">
      {/* ── Section divider ── */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-navy/10" />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-navy/100 font-heading whitespace-nowrap">
          Payment details
        </span>
        <div className="flex-1 h-px bg-navy/10" />
      </div>

      {/* ── Loading state ── */}
      {!ready && (
        <div className="flex items-center justify-center gap-3 py-8">
          <span className="w-5 h-5 border-2 border-navy/10 border-t-primary rounded-full animate-spin shrink-0" />
          <span className="text-sm text-navy/40 font-body">
            Preparing secure payment...
          </span>
        </div>
      )}

      {/* ── Elements — hidden until ready ── */}
      <div className={ready ? "block" : "hidden"}>
        <Elements stripe={stripePromise} options={elementsOptions}>
          <CheckoutForm
            amount={amount}
            currency={currency}
            email={email}
            clientSecret={clientSecret}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </Elements>
      </div>

      {/* ── Ready detector ── */}
      {!ready && (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <ReadyDetector onReady={() => setReady(true)} />
        </Elements>
      )}
    </div>
  );
};
